import { useEffect, useState, lazy, Suspense } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Map, Globe } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;

const MapLibre3D = lazy(() => import('./MapLibre3D'));

const greenIcon = new L.DivIcon({
  html: `<div style="
    width: 32px; height: 32px;
    background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)));
    border: 3px solid #fff;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    box-shadow: 0 4px 15px hsl(var(--primary) / 0.5);
  "><div style="
    width: 10px; height: 10px;
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
  "></div></div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -34],
});

function PulseMarker({ position }) {
  const map = useMap();
  useEffect(() => {
    const pulse = L.divIcon({
      html: `<div style="
        width: 60px; height: 60px;
        border: 2px solid hsl(var(--primary) / 0.4);
        border-radius: 50%;
        animation: mapPulse 2s ease-out infinite;
        position: absolute;
        top: -14px; left: -14px;
      "></div>`,
      className: '',
      iconSize: [32, 32],
    });
    const marker = L.marker(position, { icon: pulse, interactive: false }).addTo(map);
    return () => marker.remove();
  }, [map, position]);
  return null;
}

const LOCATION = [13.0511, 80.1534];
const COMPANY = 'Challenge Packaging Industries';
const ADDRESS = '332, SF, 1, Mettukuppam Rd, Visalakshi Nagar, Vanagaram, Chennai, Tamil Nadu 600116';

function ElevatedMap({ isMobile }) {
  return (
    <div
      className="map-3d-container relative w-full overflow-hidden"
      style={{
        height: isMobile ? '350px' : '500px',
        borderRadius: isMobile ? '12px' : '0',
        margin: isMobile ? '0 16px' : '0',
        width: isMobile ? 'calc(100% - 32px)' : '100%',
        boxShadow: '0 20px 60px -10px rgba(0,0,0,0.5)',
      }}
      data-testid="map-elevated-view"
    >
      <MapContainer
        center={LOCATION}
        zoom={15}
        scrollWheelZoom={true}
        zoomControl={true}
        dragging={true}
        doubleClickZoom={true}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        <Marker position={LOCATION} icon={greenIcon}>
          <Popup>
            <div style={{ minWidth: 220 }}>
              <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 700, color: 'hsl(var(--primary))' }}>{COMPANY}</h3>
              <p style={{ margin: '0 0 10px', fontSize: '12px', lineHeight: 1.5, color: '#94a3b8' }}>{ADDRESS}</p>
              <a
                href="https://maps.app.goo.gl/ShRt7FnrjFV3rpRE9"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '6px 16px',
                  background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  textDecoration: 'none',
                }}
              >
                Get Directions
              </a>
            </div>
          </Popup>
        </Marker>
        <PulseMarker position={LOCATION} />
      </MapContainer>
    </div>
  );
}

export default function InteractiveMap() {
  const [loaded, setLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState('3d'); // 'elevated' or '3d'

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'leaflet-3d-styles';
    style.textContent = `
      @keyframes mapPulse {
        0% { transform: scale(0.5); opacity: 1; }
        100% { transform: scale(2.5); opacity: 0; }
      }
      .map-3d-container .leaflet-container { background: #0a1a0f !important; }
      .map-3d-container .leaflet-control-zoom a {
        background: rgba(10,26,15,0.95) !important;
        color: hsl(var(--primary)) !important;
        border-color: hsl(var(--primary) / 0.3) !important;
        backdrop-filter: blur(8px);
      }
      .map-3d-container .leaflet-control-zoom a:hover {
        background: hsl(var(--primary) / 0.25) !important;
      }
      .map-3d-container .leaflet-control-attribution {
        background: rgba(10,26,15,0.8) !important;
        color: rgba(148,163,184,0.5) !important;
        backdrop-filter: blur(4px);
      }
      .map-3d-container .leaflet-control-attribution a { color: hsl(var(--primary) / 0.6) !important; }
      .map-3d-container .leaflet-popup-content-wrapper {
        background: rgba(10,26,15,0.95) !important;
        color: #e2e8f0 !important;
        border: 1px solid hsl(var(--primary) / 0.3) !important;
        border-radius: 12px !important;
        box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
        backdrop-filter: blur(12px);
      }
      .map-3d-container .leaflet-popup-tip { background: rgba(10,26,15,0.95) !important; }
    `;
    document.head.appendChild(style);
    setLoaded(true);
    return () => {
      const el = document.getElementById('leaflet-3d-styles');
      if (el) el.remove();
    };
  }, []);

  if (!loaded) return null;

  return (
    <section className="relative overflow-hidden" data-testid="interactive-map-section">
      <div className="py-12 text-center relative z-10 bg-background">
        <span className="badge mb-4">FIND US</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight">Our Location</h2>
        <p className="text-muted-foreground mt-2 text-sm mb-6">Visit us at our manufacturing facility in Chennai</p>

        {/* Map View Toggle */}
        <div className="inline-flex items-center rounded-xl border border-border/30 bg-card/80 backdrop-blur-sm p-1" data-testid="map-view-toggle">
          <button
            onClick={() => setViewMode('elevated')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'elevated'
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="map-toggle-elevated"
          >
            <Map className="w-4 h-4" />
            <span className="hidden sm:inline">Flat View</span>
          </button>
          <button
            onClick={() => setViewMode('3d')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === '3d'
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="map-toggle-3d"
          >
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">3D View</span>
          </button>
        </div>
      </div>

      <div className="relative">
        {viewMode === 'elevated' ? (
          <ElevatedMap isMobile={isMobile} />
        ) : (
          <Suspense fallback={
            <div className="w-full flex items-center justify-center bg-background" style={{ height: '500px' }}>
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          }>
            <MapLibre3D />
          </Suspense>
        )}

        {/* Edge fade gradients */}
        {!isMobile && (
          <>
            <div className="absolute top-0 left-0 right-0 h-24 pointer-events-none z-[500]" style={{ background: 'linear-gradient(to bottom, hsl(var(--background)), transparent)' }} />
            <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-[500]" style={{ background: 'linear-gradient(to top, hsl(var(--background)) 20%, transparent)' }} />
            <div className="absolute top-0 left-0 bottom-0 w-20 pointer-events-none z-[500]" style={{ background: 'linear-gradient(to right, hsl(var(--background)), transparent)' }} />
            <div className="absolute top-0 right-0 bottom-0 w-20 pointer-events-none z-[500]" style={{ background: 'linear-gradient(to left, hsl(var(--background)), transparent)' }} />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-20 pointer-events-none z-[499]" style={{ background: 'radial-gradient(ellipse at center, hsl(var(--primary) / 0.1), transparent 70%)', filter: 'blur(20px)' }} />
          </>
        )}
      </div>
    </section>
  );
}
